/**
 * « Échéances » — en tête de « Mon travail » (SPEC §5.7).
 *
 * Demandé le 29 septembre 2026 pour Mehdi, en terminale : combien de jours
 * avant les épreuves du bac, et quels contrôles tombent dans les deux semaines,
 * chacun avec de quoi s'y préparer d'un geste.
 *
 * **Sur « Mon travail » et non sur « Ma journée ».** « Ma journée » est un écran
 * d'écriture qui ferme les autres tant que le journal n'est pas envoyé : un
 * lien vers un exercice y ramènerait au journal. C'est ici que vit le cap
 * « mon prochain contrôle », et ici que se tire un exercice.
 *
 * **Rien n'est calculé ici.** Les jours restants viennent du serveur, comptés au
 * jour de la maison — l'horloge d'une tablette se règle à la main. Les
 * compétences d'un contrôle sont celles que le cap « mon prochain contrôle »
 * servirait pour ce jour-là : le même calcul, pas un second.
 *
 * **Un nombre de jours n'est pas un score** (invariant §14.1) : il mesure le
 * calendrier, pas l'enfant. Aucune autre valeur chiffrée n'est affichée — ni
 * réussite, ni progression, ni « prêt à N % ». Le ton est celui d'un agenda :
 * une date, un délai, et rien pour presser.
 *
 * **Le bac n'est dessiné qu'en terminale** (`profile.bac`), clé de niveau et
 * jamais de prénom. Le serveur n'en renvoie pas ailleurs non plus. Au collège,
 * le bloc garde les contrôles, et disparaît quand il n'y en a aucun : un
 * encadré vide tous les soirs est un encadré qu'on cesse de lire.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import type { CompetenceARevoir, ControleAVenir, EpreuveDuBac, MesEcheances } from "@/api/schema";
import { useSession } from "@/app/session";
import { enClairCourt } from "@/ui/dates";
import { etiquette } from "@/ui/matieres";
import { Card } from "@/ui/primitives";

type Etat = { quoi: "chargement" } | { quoi: "panne" } | { quoi: "pret"; reponse: MesEcheances };

/** « aujourd'hui », « demain », « dans 12 jours » — un délai, dit comme un agenda. */
export function delai(jours: number): string {
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return "demain";
  return `dans ${jours} jours`;
}

/** « Bac : épreuves de spécialité dans 260 jours ». */
export function ligneDuBac(epreuve: EpreuveDuBac): string {
  return `Bac : ${epreuve.libelle} ${delai(epreuve.dans_jours)}`;
}

/** Ce qui nomme un contrôle : sa matière, ou ce que l'emploi du temps en dit. */
export function nomDuControle(controle: ControleAVenir): string {
  if (controle.matiere) return etiquette(controle.matiere);
  return controle.source === "emploi-du-temps" ? "Devoir sur table" : "Contrôle";
}

/**
 * Pourquoi un contrôle n'a pas d'exercice — une phrase de constat.
 *
 * Trois causes, et elles ne demandent pas le même geste : une matière que la
 * source ne nomme pas, une matière qui ne se travaille pas ici, ou des
 * chapitres pour lesquels aucun exercice n'est prêt. Aucune n'est de la faute
 * de l'enfant, et aucune ne se dit en nombre.
 */
export function phraseSansCompetence(controle: ControleAVenir): string {
  switch (controle.sans_competence) {
    case "sans-matiere":
      return controle.source === "emploi-du-temps"
        ? "L'emploi du temps ne dit pas la matière."
        : "Le cahier de textes ne dit pas la matière.";
    case "hors-perimetre":
      return "Cette matière ne se travaille pas ici.";
    case "rien-de-pret":
      return "Pas encore d'exercice prêt pour ce contrôle.";
    default:
      return "";
  }
}

export function Echeances({ commencer }: { commencer: (c: CompetenceARevoir) => void }) {
  const { profile } = useSession();
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  useEffect(() => {
    let vivant = true;
    call("mesEcheances")
      .then((reponse) => vivant && setEtat({ quoi: "pret", reponse }))
      .catch(() => vivant && setEtat({ quoi: "panne" }));
    return () => {
      vivant = false;
    };
  }, []);

  if (etat.quoi === "chargement") return null;
  if (etat.quoi === "panne") {
    // Discret : le reste de l'écran fonctionne, et une carte rouge en tête
    // ferait croire que le travail du soir est cassé.
    return <p className="muted small">Je n'arrive pas à lire tes prochaines échéances.</p>;
  }

  const bac = profile.bac ? (etat.reponse.bac ?? []) : [];
  const controles = etat.reponse.controles ?? [];
  if (bac.length === 0 && controles.length === 0) return null;

  return (
    <Card title="Échéances">
      {bac.length > 0 ? (
        <ul className="echeances__bac">
          {bac.map((epreuve) => (
            <li key={epreuve.cle}>
              {ligneDuBac(epreuve)}
              <span className="muted small">
                {" "}
                · {epreuve.precision || enClairCourt(epreuve.le)}
                {epreuve.a_confirmer ? " (date à confirmer)" : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {controles.length === 0 ? (
        <p className="muted small">Aucun contrôle annoncé dans les deux semaines.</p>
      ) : (
        <ul className="echeances__controles">
          {controles.map((controle) => (
            <li key={`${controle.le}:${controle.source}:${controle.matiere ?? ""}`}>
              <p>
                <strong>{nomDuControle(controle)}</strong> — {enClairCourt(controle.le)},{" "}
                {delai(controle.dans_jours)}
              </p>
              {(controle.competences ?? []).length > 0 ? (
                <ul className="revision__liste">
                  {(controle.competences ?? []).map((c) => (
                    <li key={c.competency_id}>
                      <button
                        type="button"
                        className="revision__item"
                        onClick={() => commencer(c)}
                      >
                        <span className="revision__libelle">{c.libelle}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{phraseSansCompetence(controle)}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
